import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftListView = ({ 
  shifts = [], 
  onShiftClick, 
  onShiftEdit, 
  onShiftDelete,
  onBulkDelete,
  sortBy = 'date',
  sortOrder = 'asc'
}) => {
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [currentSort, setCurrentSort] = useState({ field: sortBy, order: sortOrder });

  // Use only the shifts passed from parent component (real data)
  const allShifts = shifts || [];

  // Status column removed as requested

  const handleSort = (field) => {
    const newOrder = currentSort?.field === field && currentSort?.order === 'asc' ? 'desc' : 'asc';
    setCurrentSort({ field, order: newOrder });
  };

  const sortedShifts = [...allShifts]?.sort((a, b) => {
    const { field, order } = currentSort;
    let aValue = a?.[field];
    let bValue = b?.[field];

    // Handle date sorting
    if (field === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    // Handle string sorting
    if (typeof aValue === 'string') {
      aValue = aValue?.toLowerCase();
      bValue = bValue?.toLowerCase();
    }

    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSelectShift = (shiftId) => {
    setSelectedShifts(prev => 
      prev?.includes(shiftId) 
        ? prev?.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  const handleSelectAll = () => {
    if (selectedShifts?.length === allShifts?.length) {
      setSelectedShifts([]);
    } else {
      setSelectedShifts(allShifts?.map(shift => shift?.id));
    }
  };

  const handleBulkAction = (action) => {
    if (action === 'delete' && onBulkDelete) {
      onBulkDelete(selectedShifts);
      setSelectedShifts([]);
    } else {
      console.log(`Bulk ${action} for shifts:`, selectedShifts);
      // Handle other bulk actions here
      setSelectedShifts([]);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date?.toLocaleDateString('de-DE', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getSortIcon = (field) => {
    if (currentSort?.field !== field) {
      return 'ArrowUpDown';
    }
    return currentSort?.order === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedShifts?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="CheckSquare" size={20} className="text-primary" />
              <span className="font-medium text-text-primary">
                {selectedShifts?.length} Schicht{selectedShifts?.length > 1 ? 'en' : ''} ausgewählt
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('edit')}
                iconName="Edit"
                iconPosition="left"
                className="touch-feedback"
              >
                Ausgewählte bearbeiten
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('delete')}
                iconName="Trash2"
                iconPosition="left"
                className="touch-feedback text-error hover:text-error"
              >
                Ausgewählte löschen
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedShifts([])}
                iconName="X"
                className="touch-feedback"
              >
                Zurücksetzen
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Shifts Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="p-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedShifts?.length === allShifts?.length}
                    onChange={handleSelectAll}
                    className="rounded border-border"
                  />
                </th>
                <th 
                  className="p-4 text-left cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-text-primary">Datum</span>
                    <Icon name={getSortIcon('date')} size={16} className="text-text-secondary" />
                  </div>
                </th>
                <th 
                  className="p-4 text-left cursor-pointer hover:bg-muted/80 transition-colors"
                  onClick={() => handleSort('employeeName')}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-text-primary">Mitarbeiter</span>
                    <Icon name={getSortIcon('employeeName')} size={16} className="text-text-secondary" />
                  </div>
                </th>
                <th className="p-4 text-left">
                  <span className="font-medium text-text-primary">Zeit</span>
                </th>
                <th className="p-4 text-left">
                  <span className="font-medium text-text-primary">Dauer</span>
                </th>
                <th className="p-4 text-left">
                  <span className="font-medium text-text-primary">Ort</span>
                </th>
                <th className="p-4 text-left">
                  <span className="font-medium text-text-primary">Aktionen</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedShifts?.map((shift) => (
                <tr 
                  key={shift?.id}
                  className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => onShiftClick && onShiftClick(shift)}
                >
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedShifts?.includes(shift?.id)}
                      onChange={() => handleSelectShift(shift?.id)}
                      onClick={(e) => e?.stopPropagation()}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-text-primary">
                      {formatDate(shift?.date)}
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <div className="font-medium text-text-primary">{shift?.employeeName}</div>
                      <div className="text-sm text-text-secondary">{shift?.employeeId}</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-text-primary">
                      {shift?.startTime} - {shift?.endTime}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-text-primary">{shift?.duration}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-text-primary">{shift?.location}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onShiftEdit && onShiftEdit(shift);
                        }}
                        iconName="Edit"
                        className="touch-feedback"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onShiftDelete && onShiftDelete(shift);
                        }}
                        iconName="Trash2"
                        className="touch-feedback text-error hover:text-error"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {allShifts?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Calendar" size={48} className="text-text-secondary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-text-primary mb-2">Keine Schichten geplant</h3>
            <p className="text-text-secondary mb-4">Erstellen Sie Ihre erste Schicht, um zu beginnen</p>
            <Button
              variant="default"
              onClick={() => {/* Handle create shift */}}
              iconName="Plus"
              iconPosition="left"
              className="touch-feedback"
            >
              Schicht erstellen
            </Button>
          </div>
        )}
      </div>
      {/* Pagination */}
      {allShifts?.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            {allShifts?.length} Schicht{allShifts?.length > 1 ? 'en' : ''} angezeigt
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled
              iconName="ChevronLeft"
              className="touch-feedback"
            >
              Zurück
            </Button>
            <span className="text-sm text-text-secondary px-3">Seite 1 von 1</span>
            <Button
              variant="outline"
              size="sm"
              disabled
              iconName="ChevronRight"
              className="touch-feedback"
            >
              Weiter
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftListView;